import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { findUniqueProjectByCode } from "../../project-context.js";
import { AppShell } from "../components/AppShell";
import { api } from "../lib/api";
import {
  type DayPreplanDate,
  type DayPreplanSection,
  type DayPreplanSnapshotItem,
  listFutureDayPreplans,
} from "../lib/preplans";

const AMS11_PROJECT_CODE = "ams11";

const REPORT_STATUS_LABEL: Record<string, string> = {
  draft: "Concept",
  submitted: "Ingediend",
  approved: "Goedgekeurd",
};

const ABSENCE_LABEL: Record<string, string> = {
  sickness: "Ziek",
  vacation: "Vakantie",
  leave: "Verlof",
  training: "Training",
  other: "Overig",
};

function formatDate(isoDate: string): string {
  return new Date(`${isoDate}T00:00:00`).toLocaleDateString("nl-NL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatUpdated(value?: string | null): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleString("nl-NL", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function staffingMemberText(member: DayPreplanSnapshotItem): string {
  const presence = member.presence ?? "";
  const start = member.actual_start_time;
  const end = member.actual_end_time;

  if (presence === "absent") {
    return ABSENCE_LABEL[String(member.absence_category ?? "")] ?? "Afwezig";
  }
  if (presence === "partial") {
    if (start && end) return `Deels aanwezig, ${start}–${end}`;
    if (start) return `Deels aanwezig, vanaf ${start}`;
    if (end) return `Deels aanwezig, tot ${end}`;
    return "Deels aanwezig";
  }
  if (start && end) return `Aanwezig, ${start}–${end}`;
  return "Aanwezig";
}

function StaffingSection({ section }: { section: DayPreplanSection }) {
  const updated = formatUpdated(section.updated_at);
  return (
    <div>
      <h3>
        Personeel · {section.mode === "replace" ? "complete ploeg" : "wijzigingen"}
      </h3>
      <p className="muted">
        {section.mode === "replace"
          ? "Deze lijst is de complete bekende ploeg voor deze datum."
          : "Deze regels worden op de dag zelf over de dan geldende normale ploeg gelegd."}
        {updated ? ` Laatst aangepast ${updated}.` : ""}
      </p>
      <ul>
        {section.snapshot.map((member, index) => (
          <li key={member.project_employee_id ?? `${section.day_preplan_section_id}-${index}`}>
            <strong>{member.employee_name ?? "Onbekende medewerker"}</strong>
            {`: ${staffingMemberText(member)}`}
            {member.role ? ` · ${member.role}` : ""}
          </li>
        ))}
      </ul>
    </div>
  );
}

function PreplanSection({ section }: { section: DayPreplanSection }) {
  if (section.section_type === "staffing") {
    return <StaffingSection section={section} />;
  }

  return (
    <div>
      <h3>{section.section_type}</h3>
      <p className="muted">
        {section.snapshot.length} vastgelegde {section.snapshot.length === 1 ? "regel" : "regels"}.
      </p>
    </div>
  );
}

function PreplanDayCard({ plan }: { plan: DayPreplanDate }) {
  const navigate = useNavigate();
  const reportStatus = plan.report_status ?? null;

  return (
    <section className="section">
      <div className="report-header">
        <div>
          <h2>{formatDate(plan.target_date)}</h2>
          {reportStatus
            ? (
              <span className={`report-status status-${reportStatus}`}>
                Dagrapport: {REPORT_STATUS_LABEL[reportStatus] ?? reportStatus}
              </span>
            )
            : <span className="report-status status-draft">Nog geen dagrapport</span>}
        </div>
        {plan.day_report_id && (
          <button
            type="button"
            className="secondary"
            onClick={() => navigate(`/reports/?id=${plan.day_report_id}`)}
          >
            Open dagrapport
          </button>
        )}
      </div>
      {plan.sections.map((section) => (
        <PreplanSection key={section.day_preplan_section_id} section={section} />
      ))}
    </section>
  );
}

export function PreplanningPage() {
  const [plans, setPlans] = useState<DayPreplanDate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const context = await api.getAccessContext();
        const project = findUniqueProjectByCode(context, AMS11_PROJECT_CODE);
        if (!project?.project_id) {
          throw new Error("AMS11 is niet eenduidig beschikbaar voor deze gebruiker.");
        }
        const futurePlans = await listFutureDayPreplans(project.project_id);
        if (active) setPlans(futurePlans);
      } catch (cause) {
        if (active) {
          setError(cause instanceof Error ? cause.message : "Onbekende fout");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <AppShell>
      <main className="panel">
        <h1>Voorplanning</h1>
        <p className="muted">
          Toekomstige informatie staat hier los van de dagrapporten. Pas wanneer een datum wordt
          voorbereid, neemt de dagstart de relevante voorplanning mee.
        </p>

        {loading && <p>Voorplanning laden...</p>}
        {error && <p className="error">Fout: {error}</p>}
        {!loading && !error && plans.length === 0 && (
          <div className="placeholder-panel">
            <h2>Geen voorplanning</h2>
            <p>Er is nog niets voor toekomstige rapportdatums vastgelegd.</p>
          </div>
        )}
        {!loading && !error &&
          plans.map((plan) => <PreplanDayCard key={plan.target_date} plan={plan} />)}
      </main>
    </AppShell>
  );
}
